import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Upload, Download, MoreHorizontal, Trash2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import DocumentUploadDialog from "@/components/dialogs/DocumentUploadDialog";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog";
import { toast } from "sonner";

export default function Documents() {
  const { documents, addDocument, deleteDocument } = useData();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setUploadOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleUpload = (form) => {
    addDocument(form);
    toast.success(`${form.name} uploaded`);
  };
  const handleDelete = () => {
    deleteDocument(deleteTarget.id);
    setDeleteTarget(null);
    toast.success("Document deleted");
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <PageHeader
        title="Document Center"
        subtitle="Upload, store, and manage your business documents."
        actions={
          <Button
            data-testid="upload-document-button"
            onClick={() => setUploadOpen(true)}
            className="bg-primary hover:bg-primary/90 rounded-lg btn-premium"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        }
      />

      {/* Drop zone */}
      <Card
        data-testid="document-dropzone"
        className="p-10 border-2 border-dashed border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 dark:bg-slate-900/50 rounded-xl text-center hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900 transition-colors cursor-pointer"
        onClick={() => setUploadOpen(true)}
      >
        <div className="mx-auto h-12 w-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center">
          <Upload className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        </div>
        <div className="mt-4 font-display text-base font-semibold text-slate-900 dark:text-slate-100">
          Drop files to upload
        </div>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          or click to browse — PDFs, Docs, Images up to 25MB
        </p>
      </Card>

      {/* Document list */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Your documents ({documents.length})
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="document-list">
          {documents.map((d, idx) => (
            <Card
              key={d.id}
              data-testid={`document-${d.id}`}
              style={{ ["--i"]: idx }}
              className="p-5 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 lift"
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {d.name}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {d.type} · {d.size}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`document-menu-${d.id}`}
                      className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white dark:text-slate-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      data-testid={`document-delete-${d.id}`}
                      onClick={() => setDeleteTarget(d)}
                      className="text-red-600 dark:text-red-400 focus:text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-5 flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                <span className="text-xs text-slate-500 dark:text-slate-400">Uploaded {d.uploaded}</span>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`document-download-${d.id}`}
                  onClick={() => toast.message("Demo: Download")}
                  className="h-8 rounded-lg text-xs"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSubmit={handleUpload}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.name || "document"}?`}
        description="This document will be removed from the demo session."
        onConfirm={handleDelete}
        testid="delete-document-confirm"
      />
    </div>
  );
}
